'use client';

import Modal from '@frontend/jetlend-web-ui/src/ui/modals/Modal';
import { useCallback } from 'react';
import { useActions } from '@frontend/jetlend-core/src/hooks/useActions';
import useStoreHandler from '@frontend/jetlend-core/src/hooks/useStoreHandler';
import useModalHandler from '@frontend/jetlend-core/src/hooks/useModalHandler';
import Button from '@frontend/jetlend-web-ui/src/ui/inputs/Button/Button';
import ContentTransitionWrapper from '@frontend/jetlend-web-ui/src/components/ContentTransitionWrapper/ContentTransitionWrapper';
import {
    limitPrescoringModalHandler,
    limitPrescoringStoreHandler,
} from '@app/ducks/borrowers/borrowers';
import { sendEvent } from '@app/ducks/common/analytics';

import styles from './LimitPrescoringModal.module.scss';
import {
    BorrowerLimitPrescoringModalStage,
    BorrowerLimitPrescoringStoreData,
} from '@app/models/borrowers/limitPrescoring';
import Image from '@app/components/Image/Image';
import StageCodeConfirm from './components/StageCodeConfirm/StageCodeConfirm';
import StageEmailConfirm from './components/StageEmailConfirm/StageEmailConfirm';
import StageReject from './components/StageReject/StageReject';
import StageHasAccount from './components/StageHasAccount/StageHasAccount';
import StageApprove from './components/StageApprove/StageApprove';
import StageProcess from './components/StageProcess/StageProcess';

/**
 * Модальное окно для отображения процесса и результата прескоринга лимита для заемщика
 */
export default function LimitPrescoringModal() {
    const [
        isOpen,
        ,
        close,
    ] = useModalHandler(limitPrescoringModalHandler);
    const sendEventAction = useActions(sendEvent);
    const clearStore = useActions(limitPrescoringStoreHandler.update);

    const [ data ] = useStoreHandler<BorrowerLimitPrescoringStoreData>(limitPrescoringStoreHandler);

    const didClosed = useCallback(() => {
        // Отправляем события аналитики перед закрытием
        sendEventAction('borrower--registration--close');
        sendEventAction('borrower--registration--close--email');

        clearStore({
            stage: undefined,
            limit_amount: undefined,
            rate: undefined,
            term_months: undefined,
            redirect_url: undefined,
            error: undefined,
            inn: undefined,
            company_data: undefined,
        });
        close();
    }, [ close, sendEventAction, clearStore ]);

    return (
        <Modal
            title
            noBorder
            size="fullscreen"
            closable
            isOpen={isOpen}
            onClosed={didClosed}
            contentClassName={styles['wrapper']}
            testId="limit-prescoring-modal"
        >
            <div className={styles['container']}>
                <Image src="/next/assets/logo.svg" width={200} height={50} unoptimized alt="" />

                <ContentTransitionWrapper
                    className={styles['content']}
                    transitionKey={data.stage}
                >
                    {data.stage === BorrowerLimitPrescoringModalStage.HAS_ACCOUNT && (
                        <StageHasAccount />
                    )}

                    {data.stage === BorrowerLimitPrescoringModalStage.APPROVE && (
                        <StageApprove />
                    )}

                    {data.stage === BorrowerLimitPrescoringModalStage.REJECT && (
                        <StageReject />
                    )}

                    {data.stage === BorrowerLimitPrescoringModalStage.PROCESS && (
                        <StageProcess />
                    )}

                    {data.stage === BorrowerLimitPrescoringModalStage.EMAIL_CONFIRM && (
                        <StageEmailConfirm />
                    )}

                    {data.stage === BorrowerLimitPrescoringModalStage.CODE_CONFIRM && (
                        <StageCodeConfirm />
                    )}
                </ContentTransitionWrapper>

                <Button
                    containerClassName={styles['button-container']}
                    className={styles['button']}
                    type='plain'
                    onClick={didClosed}
                >
                    Вернуться на сайт
                </Button>
            </div>
        </Modal>
    );
};